import type { LegalDocumentWords } from "./legal-words.ts";

// The Terms of Service in Spanish, a translation of legal-terms-en.ts (ADR-024, D701). The English
// text controls; change the Spanish text in the same commit as the English one.

export const TERMS_ES: LegalDocumentWords = {
  title: "Términos del servicio",
  summary:
    "Estos términos son el contrato entre tú y {company} cuando usas {product}. Cubren qué compras, cómo funcionan los reembolsos, qué puedes hacer con los datos y qué ley se aplica.",
  seoDescription:
    "Los términos para usar {product}: qué compras, reembolsos, uso de datos y la ley aplicable.",
  sections: [
    {
      id: "scope",
      heading: "1. Quiénes somos y para quién son estos términos",
      body: [
        '{product} es operado por {company}, {address} ("nosotros"). Los datos completos de la empresa están en el Aviso legal.',
        "{product} se vende a empresas y autónomos que lo usan para su oficio o profesión. Al crear una cuenta, confirmas que actúas en esa calidad y no como consumidor.",
        "Estos términos se aplican al sitio web, la aplicación y cualquier plan de pago, paquete de créditos o complemento. Tus propios términos no se aplican, aunque no nos opongamos a ellos.",
      ],
    },
    {
      id: "service",
      heading: "2. Qué hace {product}",
      body: [
        "{product} encuentra prospectos entre los seguidores, quienes dan me gusta y quienes comentan en las cuentas sociales públicas que elijas. Los enriquece con datos de contacto disponibles públicamente y te permite organizarlos y enviarlos por correo desde tus propios buzones.",
        "Solo leemos datos de perfiles públicos. No iniciamos sesión en la cuenta social de nadie para leerla, y no leemos perfiles ni mensajes privados. Obtenemos los datos a través de un proveedor especializado.",
        "{product} no está afiliado a Instagram ni a Meta Platforms, Inc., ni cuenta con su respaldo o conexión. Instagram es una marca registrada de su propietario.",
        "Una plataforma social puede cambiar lo que muestra en cualquier momento. Por eso no podemos garantizar que un perfil, campo o número de resultados determinado esté disponible.",
      ],
    },
    {
      id: "account",
      heading: "3. Tu cuenta",
      body: [
        "Debes tener al menos 18 años y dar datos verdaderos. Mantén en secreto tu contraseña y cualquier clave de API: eres responsable de lo que ocurra bajo tu cuenta.",
        "El propietario de una organización decide quién se une a ella. Los miembros actúan en nombre de la organización, y la organización queda vinculada por lo que hacen dentro de su rol.",
      ],
    },
    {
      id: "plans",
      heading: "4. Planes, créditos y pago",
      body: [
        "Los planes, sus límites y sus precios están en la página de precios en el momento de la compra. El IVA legal se añade donde corresponda.",
        "Un plan de pago se renueva por el mismo período, mensual o anual, hasta que lo canceles. Cada período empieza con los créditos del plan: los créditos del plan sin usar no se acumulan.",
        "Leer un perfil de Instagram cuesta un crédito, muestre o no el perfil una dirección de correo. Otras fuentes, cuando se ofrezcan, muestran su propio precio antes de empezar.",
        "Los paquetes de créditos, los créditos de IA y las horas de complemento que compres por separado se acumulan hasta que se usen.",
        "Pagas por adelantado, con tarjeta a través de nuestro proveedor de pagos o en bitcoin donde se ofrezca. Si un pago falla, te lo decimos y podemos pausar las funciones de pago hasta que se resuelva.",
      ],
    },
    {
      id: "refunds",
      heading: "5. Reembolsos",
      body: [
        "Reembolsamos el primer pago de un plan si lo solicitas dentro de los 14 días siguientes. Esta es nuestra propia promesa: el derecho legal de desistimiento para consumidores no se aplica a clientes empresariales.",
        "La promesa no se aplica:",
        {
          list: [
            "una vez que hayas usado el 25% de los créditos de ese plan, o 2.500 créditos, lo que ocurra primero;",
            "a una compra hecha con un cupón o un precio promocional;",
            "a una renovación: cancelar detiene la siguiente renovación en su lugar.",
          ],
        },
        "Un paquete de créditos se reembolsa dentro de los 14 días desde la compra, mientras no se haya usado ninguno de sus créditos.",
        "Escribe a {contact} desde la dirección de correo de la cuenta. Reembolsamos dentro de 14 días, al método con el que pagaste. Un pago en bitcoin se reembolsa por su valor en euros el día en que lo pagaste, a una dirección que nos indiques.",
      ],
    },
    {
      id: "disputes",
      heading: "6. Disputas de pago",
      body: [
        "Escríbenos antes de disputar un cargo con tu banco. Si abres una disputa sin preguntarnos antes y el cargo era correcto, pagas las tarifas que nuestro proveedor de pagos nos cobró por la disputa.",
        "Mientras una disputa esté abierta, podemos pausar la cuenta. Cuando termina a nuestro favor, el importe sigue siendo exigible.",
      ],
    },
    {
      id: "use",
      heading: "7. Cómo puedes usar los datos",
      body: [
        "Tú decides a quién lee {product}, qué contactos exportas y qué haces con ellos. Eres el responsable del tratamiento de esos datos de contacto y respondes de usarlos conforme a la ley.",
        "Garantizas que tienes una base legal para cada uso que hagas de ellos. Eso incluye enviar correos, guardarlos en un CRM y subirlos a una plataforma publicitaria como las audiencias personalizadas de Meta. Si te basas en el interés legítimo, lo has ponderado frente a los intereses de las personas afectadas. En particular, debes:",
        {
          list: [
            "tener una base legal conforme al RGPD, y a cualquier otra ley aplicable, para cada persona que conserves, contactes o subas;",
            "dar a cada persona en la UE la información que exige el art. 14 RGPD, incluido de dónde proceden sus datos. Hazlo en el plazo de un mes desde que los obtienes, y como tarde en tu primer contacto con ella;",
            "contar con el consentimiento previo que la ley exige antes de enviar publicidad por correo. En Alemania, el § 7 UWG lo exige también de las empresas;",
            "atender de inmediato toda objeción, baja y solicitud de supresión: deja de contactar a la persona y elimínala de tus listas, de tu CRM y de toda audiencia que hayas subido. Mantén el enlace de baja que {product} añade a cada correo;",
            "seguir los términos de los buzones, plataformas publicitarias y otros servicios que conectes o a los que subas datos, incluida cualquier garantía de base legal que te pidan.",
          ],
        },
        "Puedes subir los contactos que exportaste a una plataforma publicitaria para tu propia publicidad, si cumples las obligaciones anteriores y los términos de esa plataforma. Nunca uses categorías especiales de datos, ni nada que las revele, para elegir o describir una audiencia.",
        "No debes usar {product} para:",
        {
          list: [
            "enviar spam, ni acosar, perseguir, amenazar o engañar a nadie;",
            "dirigirte a menores, o recopilar datos de salud, políticos, religiosos u otras categorías especiales de datos;",
            "vender, alquilar, publicar o compartir listas, o los contactos que contienen, con terceros. Tu propia subida a una plataforma publicitaria, como se permite arriba, no es compartir;",
            "discriminar, o elaborar perfiles para negar a alguien vivienda, trabajo, crédito o seguro;",
            "infringir la ley, vulnerar los derechos de alguien, o enviar contenido malicioso;",
            "copiar, extraer, aplicar ingeniería inversa o sobrecargar {product}, o eludir sus límites o su seguridad.",
          ],
        },
      ],
    },
    {
      id: "processing",
      heading: "8. Tratamiento de datos en tu nombre",
      body: [
        "Para las listas que construyes, los contactos que exportas y los correos que envías, tratamos datos personales en tu nombre como tu encargado del tratamiento (art. 28 RGPD). Tú eres el responsable del tratamiento de esos datos, y de toda copia que exportes o subas a otro lugar: esta sección es nuestro acuerdo de tratamiento.",
        {
          list: [
            "Objeto y duración: los prospectos, listas, campañas y respuestas de tu espacio de trabajo, durante todo el tiempo que exista tu cuenta.",
            "Tipos de datos: datos de perfil público como nombre, usuario, biografía, número de seguidores, ubicación, sitio web, y dirección de correo o teléfono públicos. También los correos que envías y las respuestas que recibes.",
            "Personas afectadas: las personas cuyos perfiles públicos has leído, y las personas a las que envías correos.",
            "Actuamos solo según tus instrucciones documentadas, que son estos términos y los ajustes que eliges, y te avisamos si creemos que una instrucción infringe la ley.",
            "Toda persona que maneja los datos está sujeta a confidencialidad.",
            "Lo protegemos con las medidas técnicas y organizativas descritas en la Política de privacidad.",
            "Usamos a los subencargados listados en la Política de privacidad. Anunciamos uno nuevo con 30 días de antelación: si te opones por motivos razonables, puedes terminar el contrato antes de que empiece.",
            "Te ayudamos a responder a las solicitudes de las personas afectadas y a cumplir tus obligaciones de seguridad, brechas y evaluaciones de impacto.",
            "Te informamos sin demora injustificada de una brecha de datos personales que afecte a tus datos.",
            "Cuando se elimina tu cuenta, eliminamos los datos, salvo que la ley nos exija conservarlos.",
            "Te damos la información que necesitas para demostrar que se cumplen estas obligaciones, y permitimos auditorías con aviso razonable.",
          ],
        },
      ],
    },
    {
      id: "ai",
      heading: "9. Funciones de IA y agentes",
      body: [
        "Las funciones de IA sugieren texto y acciones. Pueden estar equivocadas. Revisa lo que escriben antes de enviarlo: eres responsable de lo que sale en tu nombre.",
        "Si conectas una cuenta a un agente, como un buzón o un inicio de sesión guardado en tu bóveda, el agente actúa con esa cuenta según tus instrucciones. Conecta solo cuentas que puedas usar de esta forma.",
      ],
    },
    {
      id: "ownership",
      heading: "10. Propiedad",
      body: [
        "{product}, su software y su diseño nos pertenecen. Obtienes el derecho de usarlos mientras exista tu cuenta, dentro de estos términos.",
        "Tus listas, plantillas y contenido siguen siendo tuyos. Nos permites tratarlos solo para ofrecerte {product}.",
      ],
    },
    {
      id: "term",
      heading: "11. Terminación del contrato",
      body: [
        "Puedes cancelar un plan de pago en cualquier momento desde la aplicación. Termina al final del período que pagaste, y la cuenta pasa al plan gratuito.",
        "Puedes eliminar tu cuenta en cualquier momento. Exporta antes tus listas: la eliminación las borra tras un breve período de gracia.",
        "Podemos terminar el contrato con un aviso de 30 días por correo. Entonces reembolsamos la parte que pagaste pero no recibirás.",
        "Podemos suspender o terminar una cuenta de inmediato por causa justificada. Por ejemplo, un incumplimiento grave o repetido de la sección 7, fraude, o un riesgo para {product} o para otros clientes. En ese caso no corresponde ningún reembolso.",
      ],
    },
    {
      id: "availability",
      heading: "12. Disponibilidad y cambios en el servicio",
      body: [
        "Trabajamos para mantener {product} disponible y preciso, pero no podemos garantizar que esté libre de interrupciones o errores. El mantenimiento y las caídas de las plataformas y proveedores de los que dependemos pueden interrumpirlo.",
        "Mejoramos {product} con el tiempo. No eliminamos una función de pago en la que confías durante un período ya pagado sin avisarte, y en ese caso puedes cancelar y recuperar la parte no usada.",
      ],
    },
    {
      id: "liability",
      heading: "13. Responsabilidad",
      body: [
        "Respondemos sin límite por dolo y negligencia grave, por daños a la vida, el cuerpo o la salud, y conforme a la ley alemana de responsabilidad por productos.",
        "Por negligencia leve solo respondemos si incumplimos una obligación esencial, una que hace posible el contrato y en la que puedes confiar. En ese caso, nuestra responsabilidad se limita al daño típico y previsible para este tipo de contrato.",
        "Se excluye cualquier responsabilidad adicional. Estos límites también protegen a nuestro personal y agentes.",
      ],
    },
    {
      id: "indemnity",
      heading: "14. Reclamaciones contra nosotros causadas por tu uso",
      body: [
        "Si alguien reclama contra nosotros porque usaste {product} incumpliendo estos términos o la ley, nos mantienes indemnes frente a la reclamación y los costes razonables de nuestra defensa. Esto no se aplica si no eres responsable del incumplimiento.",
        "Te informamos de tal reclamación sin demora y no la resolvemos sin hablar contigo.",
      ],
    },
    {
      id: "changes",
      heading: "15. Cambios en estos términos",
      body: [
        "Te enviamos por correo cualquier cambio en estos términos al menos 30 días antes de que entre en vigor, con el nuevo texto. Si te opones, puedes terminar el contrato antes de que el cambio se aplique.",
        "Si no te opones, el cambio se aplica desde la fecha indicada. El silencio nunca cuenta como aceptación de un precio mayor o de un cambio en lo que has comprado: eso necesita tu consentimiento expreso.",
      ],
    },
    {
      id: "law",
      heading: "16. Ley, tribunales e idioma",
      body: [
        "Se aplica la ley alemana, sin la Convención de la ONU sobre Compraventa Internacional de Mercaderías. Si al final resultas ser consumidor, siguen aplicando las protecciones obligatorias de tu país de residencia.",
        "Si eres comerciante o un organismo público, los tribunales de Berlín conocen de cualquier disputa. También podemos demandarte donde tengas tu sede.",
        "Estos términos existen en inglés y en español. Si los dos difieren, el texto en inglés prevalece.",
        "Si una cláusula es inválida, el resto permanece en vigor.",
      ],
    },
    {
      id: "contact",
      heading: "17. Contacto",
      body: ["Escríbenos a {contact}, o por correo postal a {company}, {address}."],
    },
  ],
};
